import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

interface PinButtonProps {
  isPinned: boolean;
  onTogglePin: () => Promise<void> | void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const PinButton: React.FC<PinButtonProps> = ({
  isPinned,
  onTogglePin,
  size = 'small',
  className = ''
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return; // Prevent double-clicks

    setIsAnimating(true);
    setIsLoading(true);

    try {
      await onTogglePin();
    } catch (error) {
      console.error('Error toggling pin:', error);
    } finally {
      setIsLoading(false);
      // Reset animation state after animation completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  const iconSize = size === 'small' ? 18 : size === 'medium' ? 20 : 24;

  return (
    <Tooltip title={isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'} arrow>
      <IconButton
        size={size}
        onClick={handleClick}
        disabled={isLoading}
        className={className}
        sx={{
          transition: 'transform 0.15s ease, color 0.15s ease',
          transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
          color: isPinned ? 'primary.main' : 'action.active',
          '&:hover': {
            color: isPinned ? 'primary.dark' : 'primary.light',
            transform: 'scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={iconSize} />
        ) : isPinned ? (
          <PushPinIcon sx={{ fontSize: iconSize }} />
        ) : (
          <PushPinOutlinedIcon sx={{ fontSize: iconSize }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default PinButton;
